import { IpaType } from "../phonemes/ipa";
import { And, Change, Condition, isOr, Not, Or } from "./change";

/**
 * parse System PIE's foma rules for a language,
 * not general because it makes lots of assumptions about the format
 */
export function parseFoma(s: string): Change[] {
  const defines: Defines = {};
  const rules: Record<string, Change[]> = {};
  const changes: Change[] = [];

  // cheat to make it easier to parse "not" by getting rid of spaces after \
  // and assume that quotes aren't needed (e.g., for "-")
  const cheat = s.replace(/\\ */g, '\\').replace(/"/g, '');

  const lines = cheat.split('\n');
  for (const line of lines) {
    const end = line.indexOf(';');
    if (end === -1 || line.length < 3 || line[0] === '#') {
      continue;
    }
    const content = line.substring(0, end);

    if (content.startsWith('define ')) {
      const endOfName = content.indexOf(' ', 7);
      const name = content.substring(7, endOfName);
      const remainder = content.substring(endOfName).trim();
      if (content.indexOf(' -> ') === -1) {
        // e.g., 'define Thing [a|b]'
        defines[name] = parseOr(remainder, defines)
      } else {
        // e.g., 'define Thing a -> b , c -> d
        const pieces = remainder.split(',');
        const results = pieces.map(piece => parseRule(piece.trim(), defines));
        rules[name] = results;
        results.forEach(rule => changes.push(rule));
      }
    } else if (content.startsWith('chain ')) {
      // e.g., 'chain Thing, OtherThing'
      const pieces = content.substring(6).trim().split(',');
      const results = pieces.flatMap(name => rules[name.trim()]);
      return results;
    }
  }
  return changes;
}

type Defines = Record<string, string | Or>;

function parseRule(s: string, defines: Defines): Change {
  const input: And = [];
  const output: IpaType[] = [];
  const preList: And = [];
  const postList: And = [];
  let preOr = false;
  let postOr = false;

  let stage = 0;
  const pieces = split(s);
  for (const piece of pieces) {
    if (piece === '->') {
      stage = 1;
    } else if (piece === '||') {
      stage = 2;
    } else if (piece === '_') {
      stage = 3;
    } else if (stage === 0) { // input
      const item = interpret(piece, defines);
      input.push(item);
    } else if (stage === 1) { // output
      if (piece !== '0') {
        output.push(piece);
      }
    } else if (stage === 2) { // pre
      if (piece === '|') {
        preOr = true; // list should be interpreted as an OR
      } else {
        const item = interpret(piece, defines);
        preList.push(item);
      }
    } else {
      if (piece === '|') {
        postOr = true; // list should be interpreted as an OR
      } else {
        const item = interpret(piece, defines);
        postList.push(item);
      }
    }
  }

  const pre: Condition | null = preOr ? [{ or: preList as IpaType[] }] :
    preList.length === 1 && preList[0] === '#boundary' ? '#boundary' :
      preList.length > 0 ? preList : null;
  const post: Condition | null = postOr ? [{ or: postList as IpaType[] }] :
    postList.length === 1 && postList[0] === '#boundary' ? '#boundary' :
      postList.length > 0 ? postList : null;
  if (pre && post) {
    return { input, output, pre, post };
  } else if (pre) {
    return { input, output, pre };
  } else if (post) {
    return { input, output, post };
  }
  return { input, output };
}

/** split a string into pieces that can be recursively parsed */
function split(s: string): string[] {
  const list: string[] = [];
  let current = '';
  for (let i = 0; i < s.length; i++) {
    const c = s.charAt(i);
    if (c === ' ') {  // space -> add current to list
      if (current.length > 0) {
        list.push(current);
        current = '';
      }
    } else if (c === '[') { // [ -> put entire bracketed string in list
      if (current.length > 0 && current !== '\\') {
        list.push(current);
        current = '';
      }
      const end = s.indexOf(']', i + 1);  // assumes non-nested []
      current += s.substring(i, end + 1);
      i = end;
    } else {  // else add to current string
      current += c;
    }
  }
  if (current.length > 0) { // add leftovers
    list.push(current);
  }
  return list;
}

/** figure out how to interpet this piece: as an Or, a lookup in 'define', or a string */
function interpret(piece: string, defines: Defines): string | Or | Not {
  if (piece[0] === '[') {
    return parseOr(piece, defines);
  } else if (piece[0] === '\\') {
    const actualPiece = piece.substring(1);
    if (actualPiece[0] === '[') {
      const value = parseOr(actualPiece, defines);
      return { not: value };
    } else {
      const value = defines[actualPiece] ? defines[actualPiece] : actualPiece;
      return { not: value };
    }
  } else if (piece === '.#.') {
    return '#boundary';
  }
  return defines[piece] ? defines[piece] : piece;
}

function parseOr(s: string, defines: Defines): string | Or {
  const end = s.indexOf(']');
  if (s[0] !== '[' || end === -1) {
    return s;
  }
  const list = s.substring(1, end).split('|');

  // if any items are a define, extract its list & include that
  const or = list.flatMap(l => {
    const piece = l.trim();
    const item = defines[piece] ? defines[piece] : piece;
    // note: assumes item.or isn't a Not
    return isOr(item) ? item.or as string[] : item;
  });
  return { or };
}
